import { DetailedPageMarketingBanner } from '../../../domain/detailed-page-marketing-banner';
import { CreateMarketingDetailPageItemApiModel, GenericLayoutApiModel, MarketingDetailPageItemApiModel } from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  buttonMapperDomainToApi,
  countryTargetingMapper,
  localizedBackgroundImagesMapper,
  platformTargetingMapper,
  scheduledDateMapperDomainToApi,
  userTargetingMapper,
} from '../../mapping/create-tray-domain-to-api';
import { MarketingItemLayout } from '../../../domain/domain-layouts';
import {
  backgroundImagesMapperApiToDomain,
  buttonMapperApiToDomain,
  commonMapper,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { DetailedPageMarketingBannerViewModel } from './detailed-page-marketing-banner-form.view-model';
import { SchedulingType } from '../../../shared/enums/scheduling-type.enum';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  mapDomainButtonToButtonViewModel,
  mapLocalizedDomainImagesToPlatformLocalizedViewModelImage,
  mapMarketingItemLayoutToTraySelectorType,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  mapMarketingButtonToDomainButton,
  mapTraySelectorTypeToMarketingItemLayout,
  mapViewModelImagesToLocalizedDomainImages,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'DetailedPageMarketingBanner'

/**
 * DOMAIN -> API
 */

const mapDomainDetailedPageBannerLayoutToApiLayout = (domainLayout: MarketingItemLayout): GenericLayoutApiModel => {
  switch (domainLayout) {
    case MarketingItemLayout.DETAILED_PAGE_BANNER_LEFT:
      return GenericLayoutApiModel.Left;
    case MarketingItemLayout.DETAILED_PAGE_BANNER_CENTER:
      return GenericLayoutApiModel.Centered;
    case MarketingItemLayout.DETAILED_PAGE_BANNER_RIGHT:
      return GenericLayoutApiModel.Right;
  }
};

export const mapDomainDetailedPageMarketingBannerToApiModel = (
  banner: DetailedPageMarketingBanner,
  pageVersionId: string,
  tenantId?: string
): CreateMarketingDetailPageItemApiModel => {
  return remainderMapper(
    mapperPipe(
      banner,
      countryTargetingMapper,
      buttonMapperDomainToApi,
      scheduledDateMapperDomainToApi,
      userTargetingMapper,
      localizedBackgroundImagesMapper,
      platformTargetingMapper
    ),
    () => ({
      type: 'DetailedPageMarketingBanner',
      pageVersionId,
      name: banner.titleEnglish,
      layout: mapDomainDetailedPageBannerLayoutToApiLayout(banner.template),
      headlineLocalized: banner.marketingText,
      tenantId,
    })
  );
};

/**
 * API -> DOMAIN
 */

const mapApiDetailedPageBannerLayoutToDomain = (template: GenericLayoutApiModel): MarketingItemLayout => {
  switch (template) {
    case GenericLayoutApiModel.Left:
      return MarketingItemLayout.DETAILED_PAGE_BANNER_LEFT;
    case GenericLayoutApiModel.Right:
      return MarketingItemLayout.DETAILED_PAGE_BANNER_RIGHT;
    case GenericLayoutApiModel.Centered:
      return MarketingItemLayout.DETAILED_PAGE_BANNER_CENTER;
  }
};

export const mapApiDetailedPageMarketingBannerToDomain = (banner: MarketingDetailPageItemApiModel): DetailedPageMarketingBanner => {
  return remainderMapper(
    mapperPipe(
      banner,
      commonMapper,
      buttonMapperApiToDomain,
      targetingMapperApiToDomain,
      scheduledDateMapperApiToDomain,
      backgroundImagesMapperApiToDomain
    ),
    () => ({
      type: 'DetailedPageMarketingBanner',
      titleEnglish: banner.name,
      template: mapApiDetailedPageBannerLayoutToDomain(banner.layout),
      marketingText: banner.headlineLocalized,
    })
  );
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapDomainDetailedPageMarketingBannerToViewModel = (
  banner: DetailedPageMarketingBanner
): DetailedPageMarketingBannerViewModel => {
  return {
    id: banner.id,
    button: mapDomainButtonToButtonViewModel(banner.primaryButton),
    scheduling: {
      schedulingType: banner.scheduledFrom || banner.scheduledTo ? SchedulingType.Schedule : SchedulingType.LiveWhenPublished,
      scheduledFrom: banner.scheduledFrom ? new Date(banner.scheduledFrom).toISOString() : undefined,
      scheduledTo: banner.scheduledTo ? new Date(banner.scheduledTo).toISOString() : undefined,
    },
    marketingText: banner.marketingText,
    type: TrayFormViewModelType.DetailedPageMarketingBanner,
    userSegmentIds: banner.userSegmentIds,
    countryIds: banner.countryIds,
    titleEnglish: banner.titleEnglish,
    template: mapMarketingItemLayoutToTraySelectorType(banner.template),
    backgroundImages: mapLocalizedDomainImagesToPlatformLocalizedViewModelImage(banner.backgroundImages),
  };
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapViewModelDetailedPageMarketingBannerToDomain = (
  viewModel: DetailedPageMarketingBannerViewModel,
  platformIds: Record<PlatformType, string>
): DetailedPageMarketingBanner => {
  return {
    id: viewModel.id,
    type: 'DetailedPageMarketingBanner',
    titleEnglish: viewModel.titleEnglish,
    order: undefined,
    visible: undefined,
    draggable: true,
    marketingText: viewModel.marketingText,
    userSegmentIds: viewModel.userSegmentIds,
    countryIds: viewModel.countryIds,
    scheduledFrom: viewModel.scheduling?.scheduledFrom ? new Date(viewModel.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: viewModel.scheduling?.scheduledTo ? new Date(viewModel.scheduling.scheduledTo).getTime() : undefined,
    primaryButton: mapMarketingButtonToDomainButton(viewModel.button),
    backgroundImages: mapViewModelImagesToLocalizedDomainImages(viewModel.backgroundImages, platformIds),
    template: mapTraySelectorTypeToMarketingItemLayout(viewModel.template),
    platformIds: Object.values(platformIds),
  };
};
